import React, { useCallback, useState } from 'react';
import Flag from 'react-world-flags';
import { colors } from '../../constants';
import { getSplitText, getEllipsisedString } from '../../utils';
import { Tooltip } from '../Tooltip';
import { FallbackImage } from '../FallbackImage';
import {
  StyledIconLoader,
  FlagWrapper,
  IconWrapper,
  TextWrapper,
  Header,
  KeyValueWrapper,
  KeyValueSearchResults,
  TickerCompanySearchResult,
  TickerCompanySearchResultLableWrapper,
  TickerCompanySearchResultLabel,
  TickerCompanySearchResultTextWrapper,
  TickerCompanySearchResultText,
  DashboardWrapper,
  DashboardSearchResults,
  DashboardSearchResult,
  MoreButtonContainer,
  MoreButton,
} from './renderersStyles';
import { iconsMap } from './iconsMap';

const getDisplayString = (str: string, searchValue: string, isTextBox?: boolean) => {
  return str?.toLowerCase().includes(searchValue.toLowerCase()) ? getSplitText(str, searchValue, isTextBox) : str;
};

export const KeyValue: any = ({
  name,
  searchValue,
  handleSearchResultClick,
  searchResults,
  isSolo,
  keysToNames,
}: any) => {
  const [resultsPointer, setResultsPointer] = useState(10);
  const [showImage, setShowImage] = useState(false);

  const handleMoreClick = useCallback(() => {
    setResultsPointer(resultsPointer + 10);
  }, [resultsPointer]);

  setTimeout(() => setShowImage(true), 25);

  return (
    searchResults?.length > 0 && (
      <KeyValueWrapper>
        {!isSolo && <Header>{keysToNames[name]} </Header>}
        <KeyValueSearchResults>
          {searchResults.slice(0, resultsPointer)?.map((t: any, index: number) => {
            const ellipsisedName = getEllipsisedString(t.name, '14px Montserrat', t.text ? 180 : 660);
            const ellipsisedText = getEllipsisedString(t.text, '14px Montserrat', 470);

            const hasNameTooltip = ellipsisedName !== t.name;
            const hasTextTooltip = ellipsisedText !== t.text;

            const color = colors[Math.floor(Math.random() * 57)];
            const fallbackStockIconColor = getComputedStyle(document.documentElement).getPropertyValue(color);
            return (
              <TickerCompanySearchResult
                className="searchResult"
                id="searchResult"
                key={index}
                onClick={() => handleSearchResultClick(t)}
              >
                {t.flag && (
                  <FlagWrapper id="sr">
                    {showImage ? <Flag id="sr" width="18" height="12" code={t.flag} /> : <StyledIconLoader />}
                  </FlagWrapper>
                )}
                {t.icon && <IconWrapper id="sr">{showImage ? iconsMap[t.icon] : <StyledIconLoader />}</IconWrapper>}
                {t.iconPath &&
                  (showImage ? (
                    <FallbackImage
                      src={t.iconPath?.replace('{Label}', t.name)}
                      fallbackImage={
                        <div className="fallbackImage" style={{ background: fallbackStockIconColor }}>
                          {t.name.substring(0, 2)}
                        </div>
                      }
                      initialImage={<StyledIconLoader />}
                      alt={t.name}
                      className="stockIcon"
                    />
                  ) : (
                    <StyledIconLoader className="stockIcon" />
                  ))}
                <TickerCompanySearchResultLableWrapper $hasText={t.text ? 1 : 0}>
                  <TickerCompanySearchResultLabel $hasText={t.text ? 1 : 0} id="sr">
                    {hasNameTooltip ? (
                      <Tooltip placement="top" html={getDisplayString(t.name, searchValue)}>
                        <span>{getDisplayString(ellipsisedName, searchValue)}</span>
                      </Tooltip>
                    ) : (
                      getDisplayString(ellipsisedName, searchValue)
                    )}
                  </TickerCompanySearchResultLabel>
                </TickerCompanySearchResultLableWrapper>
                <TickerCompanySearchResultTextWrapper id="sr" $hasText={t.text ? 1 : 0}>
                  <TickerCompanySearchResultText id="sr">
                    {hasTextTooltip ? (
                      <Tooltip placement="top" html={getDisplayString(t.text, searchValue, true)}>
                        <span>{getDisplayString(ellipsisedText, searchValue)}</span>
                      </Tooltip>
                    ) : (
                      getDisplayString(ellipsisedText, searchValue)
                    )}
                  </TickerCompanySearchResultText>
                </TickerCompanySearchResultTextWrapper>
              </TickerCompanySearchResult>
            );
          })}
          {resultsPointer < searchResults.length && (
            <MoreButtonContainer>
              <MoreButton onClick={handleMoreClick}>Show more</MoreButton>
            </MoreButtonContainer>
          )}
        </KeyValueSearchResults>
      </KeyValueWrapper>
    )
  );
};

export const ArrowString: any = ({
  name,
  searchValue,
  handleSearchResultClick,
  getSplitText,
  searchResults,
  isSolo,
  keysToNames,
}: any) => {
  const [resultsPointer, setResultsPointer] = useState(10);

  const handleMoreClick = useCallback(() => {
    setResultsPointer(resultsPointer + 10);
  }, [resultsPointer]);

  return (
    searchResults.length > 0 && (
      <DashboardWrapper>
        {!isSolo && <Header>{keysToNames[name]} </Header>}
        <DashboardSearchResults>
          {searchResults.length > 0 ? (
            searchResults.slice(0, resultsPointer).map((t: any, index: number) => {
              return (
                <DashboardSearchResult
                  className="searchResult"
                  id="searchResult"
                  key={index}
                  onClick={() => handleSearchResultClick(t)}
                >
                  <IconWrapper id="sr">{iconsMap[t.icon]}</IconWrapper>
                  <TextWrapper id="sr">
                    {t.name.toLowerCase().includes(searchValue.toLowerCase())
                      ? getSplitText(t.name.split('.').join(' ➝ '), searchValue)
                      : t.name.split('.').join(' ➝ ')}
                  </TextWrapper>
                </DashboardSearchResult>
              );
            })
          ) : (
            <DashboardSearchResult id="searchResult">No Results</DashboardSearchResult>
          )}
          {resultsPointer < searchResults.length && (
            <MoreButtonContainer>
              <MoreButton onClick={handleMoreClick}>Show more</MoreButton>
            </MoreButtonContainer>
          )}
        </DashboardSearchResults>
      </DashboardWrapper>
    )
  );
};

export const KeyValueInTable: any = ({ cellData, rowData, searchValue, onRowClick, showImage }: any) => {
  const name = cellData;
  const { appId, date, flag, iconPath, icon, path, text } = rowData;

  const ellipsisedName = getEllipsisedString(name, '14px Montserrat', text ? 180 : 660);
  const ellipsisedText = getEllipsisedString(text, '14px Montserrat', 470);

  const hasNameTooltip = ellipsisedName !== name;
  const hasTextTooltip = ellipsisedText !== text;

  const color = colors[Math.floor(Math.random() * 57)];
  const fallbackStockIconColor = getComputedStyle(document.documentElement).getPropertyValue(color);

  const clickTemplate = {
    appId,
    date,
    flag,
    iconPath,
    name,
    path,
    text,
  };

  return (
    <KeyValueWrapper>
      <KeyValueSearchResults>
        <TickerCompanySearchResult className="searchResult" id="searchResult" onClick={() => onRowClick(clickTemplate)}>
          {!showImage && (flag || icon || iconPath) && <StyledIconLoader />}
          {showImage && flag && (
            <FlagWrapper id="sr">
              <Flag id="sr" width="18" height="12" code={flag} />
            </FlagWrapper>
          )}
          {showImage && icon && <IconWrapper id="sr">{iconsMap[icon]}</IconWrapper>}
          {showImage && iconPath && (
            <FallbackImage
              src={iconPath?.replace('{Label}', name)}
              fallbackImage={
                <div className="fallbackImage" style={{ background: fallbackStockIconColor }}>
                  {name.substring(0, 2)}
                </div>
              }
              initialImage={<StyledIconLoader />}
              alt={name}
              className="stockIcon"
            />
          )}
          <TickerCompanySearchResultLableWrapper $hasText={text ? 1 : 0}>
            <TickerCompanySearchResultLabel $hasText={text ? 1 : 0} id="sr">
              {hasNameTooltip ? (
                <Tooltip placement="top" html={getDisplayString(name, searchValue)}>
                  <span>{getDisplayString(ellipsisedName, searchValue)}</span>
                </Tooltip>
              ) : (
                getDisplayString(ellipsisedName, searchValue)
              )}
            </TickerCompanySearchResultLabel>
          </TickerCompanySearchResultLableWrapper>
          <TickerCompanySearchResultTextWrapper id="sr" $hasText={text ? 1 : 0}>
            <TickerCompanySearchResultText id="sr">
              {hasTextTooltip ? (
                <Tooltip placement="top" html={getDisplayString(text, searchValue, true)}>
                  <span>{getDisplayString(ellipsisedText, searchValue)}</span>
                </Tooltip>
              ) : (
                getDisplayString(ellipsisedText, searchValue)
              )}
            </TickerCompanySearchResultText>
          </TickerCompanySearchResultTextWrapper>
        </TickerCompanySearchResult>
      </KeyValueSearchResults>
    </KeyValueWrapper>
  );
};

export const ArrowStringInTable: any = ({ cellData, rowData, searchValue, onRowClick }: any) => {
  const name = cellData;
  const { appId, date, flag, iconPath, icon, path, text } = rowData;

  const clickTemplate = {
    appId,
    date,
    flag,
    iconPath,
    name,
    path,
    text,
  };

  return (
    <DashboardWrapper>
      <DashboardSearchResults>
        <DashboardSearchResult className="searchResult" id="searchResult" onClick={() => onRowClick(clickTemplate)}>
          <IconWrapper id="sr">{iconsMap[icon]}</IconWrapper>
          <TextWrapper id="sr">
            {name?.toLowerCase().includes(searchValue.toLowerCase())
              ? getSplitText(name.split('.').join(' ➝ '), searchValue)
              : name?.split('.').join(' ➝ ')}
          </TextWrapper>
        </DashboardSearchResult>
      </DashboardSearchResults>
    </DashboardWrapper>
  );
};
